import * as logger from "./utils/logger";
import { IFeatureFlagStatus } from "./store/data/types";
import { FeatureFlagName } from "./utils/featureFlagName";
import { DriveStore } from "./store/reducers";
import constants from "./constants";
import { isNetworkError } from "./utils/errorHandler/error.types";
import { createTimeoutSignal } from "./sync/common";
import { trackedFetch } from "./utils/trackedFetch";
import { _originalLocalStorage } from "./utils/storage/storage.base";
import { DRIVE_STORAGE_PREFIX } from "./store/storageConstants";

const FEATURE_FLAG_STORAGE = `${ DRIVE_STORAGE_PREFIX }@featureFlag`;
const FETCH_URL = `https://${ self.location.host }/apiv1/`;
const MAX_FETCH_FEATURE_FLAGS_RETRY_ATTEMPTS = 1;

enum ErrorCodes {
    ConnectionError = 0,
    BadRequest = 400,
    Forbidden = 403,
    NotFound = 404
}

function featureFlagStatus (flag: FeatureFlagName, status: boolean): IFeatureFlagStatus {
    return { name: flag, isEnabled: status } as IFeatureFlagStatus;
}

export function emptyFeatureFlagLocalStorage () {
    _originalLocalStorage.setItem(FEATURE_FLAG_STORAGE, "{}");
    return {};
}

function isFeatureFlagsType (response: any): boolean {
    let res = response?.result;
    if (Array.isArray(res) && res.every(flag => typeof flag === "object" && flag !== null && "name" in flag && "isEnabled" in flag)) {
        return true;
    }
    return false;
}

export function getStoredFeatureFlags() {
    let storage: string | null = _originalLocalStorage?.getItem(FEATURE_FLAG_STORAGE) ?? null;

    if (storage === null) {
        return {};
    }

    try {
        return JSON.parse(storage);
    } catch (error) {
        logger.error(logger.Source.ERROR, `JSON Parsing error ${ error.message }`);
        return {};
    }
}

function setLocalStorageFeatureFlags(flags: IFeatureFlagStatus[], useLocalStorage: boolean): IFeatureFlagStatus[] {
    try {
        return flags.map(flag => {
            if (useLocalStorage){
                const storeData = getStoredFeatureFlags();
                storeData[flag.name] = featureFlagStatus(flag.name as FeatureFlagName, flag.isEnabled);

                _originalLocalStorage.setItem(FEATURE_FLAG_STORAGE, JSON.stringify(storeData));
            }
            return featureFlagStatus(flag.name as FeatureFlagName, flag.isEnabled);
        });
    } catch (error) {
        logger.error(logger.Source.ERROR, `Cannot set feature flag in local storage ${ error.message }, local storage flag: ${ useLocalStorage }`);
        return flags.map(flag => featureFlagStatus(flag.name as FeatureFlagName, false));
    }
}

function getFeatureFlagCache(flag: FeatureFlagName, store: DriveStore | undefined, useCaching: boolean): IFeatureFlagStatus {
    if (!useCaching) {
        return featureFlagStatus(flag, false);
    }

    let featureFlag: IFeatureFlagStatus | undefined;
    if (!store) {
        featureFlag = getStoredFeatureFlags()[flag];
    } else {
        featureFlag = store.getState().data.featureFlags[flag];
    }

    return featureFlag ? featureFlag : featureFlagStatus(flag, false);
}

function logFailedResponse(response: Response, flags: string): void {
    if (response.status >= 500){
        logger.log(logger.Source.ERROR, `A server issue occurred when fetching ${ flags } feature flags`);
        return;
    }
    switch (response.status) {
        case ErrorCodes.ConnectionError:
            logger.log(logger.Source.ERROR, `Connection failure when fetching ${ flags } feature flags`);
            break;
        case ErrorCodes.Forbidden:
            logger.log(logger.Source.ERROR, `Forbidden response when requesting ${ flags } feature flags`);
            break;
        case ErrorCodes.NotFound:
            logger.log(logger.Source.ERROR, `Not found error when fetching ${ flags } feature flags`);
            break;
        case ErrorCodes.BadRequest:
            logger.log(logger.Source.ERROR, `Bad request failure when fetching ${ flags } feature flags`);
            break;
        default:
            logger.error(logger.Source.ERROR, `Server response is ${ response.status } when fetching ${ flags } feature flags`);
            break;
    }
}

function logFailedFetch (error, flags: string){
    if (error.name.includes("AbortError")) {
        logger.log(logger.Source.ERROR, `${ flags } feature flag request has timed out`);
    } else if (navigator.onLine && error.name.includes("Failed to fetch")) {
        logger.error(logger.Source.ERROR, `Failed to fetch ${ flags } feature flag, device is online ${ error.message }`);
    } else if (error.name.includes("is not valid JSON")) {
        logger.error(logger.Source.ERROR, `JSON parsing error when requesting ${ flags } feature flag ${ error.message }`);
    } else if (!navigator.onLine && error.name.includes("Failed to fetch")) {
        logger.log(logger.Source.ERROR, `Failed to fetch ${ flags } feature flag, device offline ${ error.message }`);
    } else {
        logger.error(logger.Source.ERROR, `Unexpected error when requesting ${ flags } feature flag ${ error.message }`);
    }
}

/**
 * The function will make an unauthenticated call to 'GetFeatureFlag' endpoint which will return the status of a given feature flag.
 *
 * The utility will attempt to store the result if there is no redux store the requested fails:
 * 1. If there is no store available, local storage will be **written to** and **read from**
 * 2. If there is a redux store, the store will be **checked** but not written to. The preference when writing to FeatureFlag store is
 * to use the functions within FeatureFlags.ts
 * @param flags feature flags that will be fetched
 * @param databaseName optional database to request
 * @param store optional Drive Store for reading from the redux store
 * @param useCaching for situations where local storage cannot be used such as service worker, it will refrain from writing or reading from local storage
 */
async function isFeatureFlagsEnabled(flags: FeatureFlagName[], databaseName?: string, store?: DriveStore, useCaching = true, retryAttempt = 0): Promise<IFeatureFlagStatus[]> {
    let calls: { method: string; params: {flag: string; database?: string } }[] = [];
    flags.forEach(flag => {
        calls.push({
            method: "GetFeatureFlag",
            params: {
                flag,
                database: databaseName || ""
            }
        });
    });
    let options = {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
        },
        body: JSON.stringify({
            method: "ExecuteMultiCall",
            params: {
                calls
            }
        })
    };
    try {
        const timeoutPromise = createTimeoutSignal("AbortError: Request has timed out", constants.IS_FEATURE_FLAG_ENABLED_TIMEOUT);
        const fetchPromise = trackedFetch(FETCH_URL, options);

        let response: any = await Promise.race([fetchPromise, timeoutPromise]);
        if (!response.ok) {
            logFailedResponse(response, flags.join(", "));
            return flags.map(flag => getFeatureFlagCache(flag, store, useCaching));
        }

        const data: any = await response.json();

        if (isFeatureFlagsType(data)) {
            return store  ? data.result as IFeatureFlagStatus[] : setLocalStorageFeatureFlags(data.result, useCaching);
        } else if (data?.error) {
            let errorObject = data.error;
            const requestIndex = errorObject.data.requestIndex;
            if (typeof requestIndex === "number" && requestIndex < flags.length) {
                // Handle partial failure when using ExecuteMulticall
                logger.log(logger.Source.ERROR, `ExecuteMulticall failed when fetching ${ flags[requestIndex] } feature flag`);
                const result = store ? [featureFlagStatus(flags[requestIndex], false)] : setLocalStorageFeatureFlags([featureFlagStatus(flags[requestIndex], false)], useCaching);
                if (flags.length > 1 && retryAttempt < MAX_FETCH_FEATURE_FLAGS_RETRY_ATTEMPTS) {
                    const remainingFlags = await isFeatureFlagsEnabled(flags.filter((_, index) => index !== requestIndex), databaseName, store, useCaching, retryAttempt + 1);
                    return result.concat(remainingFlags); // result is concatinated not in the original order
                }
            } else {
                logger.error(logger.Source.ERROR, `There is an API error when fetching ${ flags.join(", ") } feature flag: ${ errorObject }`);
            }
        } else {
            logger.error(logger.Source.ERROR, `Malformed server response: ${ JSON.stringify(data) }`);
        }
        return flags.map(flag => getFeatureFlagCache(flag, store, useCaching));
    } catch (error) {
        if (isNetworkError(error)) {
            logger.log(logger.Source.ERROR, `There is a network error when fetching ${ flags.join(", ") } feature flag`);
            if (retryAttempt < MAX_FETCH_FEATURE_FLAGS_RETRY_ATTEMPTS) {
                const retryResponse = await isFeatureFlagsEnabled(flags, databaseName, store, useCaching, retryAttempt + 1);
                return retryResponse;
            }
        }
        logFailedFetch(error, flags.join(", "));
        return flags.map(flag => getFeatureFlagCache(flag, store, useCaching));
    }
}

export default isFeatureFlagsEnabled;